import { motion, useReducedMotion } from 'motion/react';
import { Wordmark } from '../components/Logo';
import { Footer } from '../components/ui';
import { CONTACT_WA, TIERS } from '../lib/site';
import { Link } from 'react-router-dom';

const fade = (reduce: boolean | null) => ({
  initial: reduce ? undefined : { opacity: 0, y: 18 },
  whileInView: reduce ? undefined : { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
});

export default function Landing() {
  const reduce = useReducedMotion();
  const F = fade(reduce);
  return (
    <main>
      <header className="container flex items-center justify-between py-6">
        <Wordmark height={30} />
        <nav className="flex items-center gap-2 md:gap-4 text-sm font-semibold">
          <a href="#how" className="hidden md:inline hover:underline">كيف يعمل</a>
          <a href="#pricing" className="hidden md:inline hover:underline">الأسعار</a>
          <a href="#faq" className="hidden md:inline hover:underline">أسئلة</a>
          <Link to="/start" className="btn btn-primary">ابدأ بمحلك</Link>
        </nav>
      </header>

      {/* HERO */}
      <section className="container section pt-6 md:pt-12">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <motion.div {...F} className="md:col-span-7">
            <div className="eyebrow mb-4">لمحلات الحلاقة الرجالية في الرياض</div>
            <h1 className="display" style={{ maxWidth: '12ch' }}>دخلك ثابت أول الشهر. وزبونك ثابت على كرسيك.</h1>
            <p className="mt-6 text-lg md:text-xl muted" style={{ maxWidth: '46ch' }}>
              تيمبو نظام عضويات شهرية لمحلك. الزبون يدفع مقدمًا مقابل حلاقاته، تصله بطاقة على واتساب، والحلاق يسجل الزيارة بزر واحد. الأموال تدخل حسابك أنت، مباشرة، والسعر تحدده أنت.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/start" className="btn btn-accent btn-lg">ابدأ بمحلك الآن</Link>
              <a href="#how" className="btn btn-ghost btn-lg">شف كيف يعمل</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm muted">
              <span className="beat ps-5">بلا تطبيق للزبون</span>
              <span className="beat ps-5">بلا نسبة من اشتراكات زبائنك</span>
              <span className="beat ps-5">الدفع عندك في المحل كما هو اليوم، بلا بوابة ولا عمولة</span>
              <span className="beat ps-5">قاعدة عملائك عندك، وترسل لهم عروضك على واتساب بموافقتهم</span>
            </div>
          </motion.div>
          <div className="md:col-span-5">
            <HeroArt reduce={!!reduce} />
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section bg-ink text-paper">
        <div className="container grid md:grid-cols-12 gap-8">
          <motion.div {...F} className="md:col-span-4">
            <div className="eyebrow">المشكلة كما يقولها صاحب المحل</div>
            <h2 className="mt-3 text-paper">محل مزدحم بالليل، وفاضي بالنهار، ودخله مفاجأة كل شهر.</h2>
          </motion.div>
          <div className="md:col-span-8 grid gap-4">
            {[
              'أعرف دخل اليوم بعد ما يسكّر المحل. أما دخل الشهر الجاي، فالله أعلم.',
              'الحلاقين جالسين من 9 الصبح إلى 2 الظهر، والإيجار والرواتب ماشية.',
              'عندي زبائن من سنين، وما عندي أسماءهم ولا أرقامهم في مكان واحد. اللي اختفى، ما أدري إنه اختفى.',
            ].map((q, i) => (
              <motion.blockquote key={i} {...F} transition={{ ...F.transition, delay: i * 0.08 }} className="rounded-[var(--radius-lg)] border border-paper/15 p-6 text-lg leading-relaxed">
                «{q}»
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="container section">
        <motion.div {...F} className="max-w-2xl">
          <div className="eyebrow">كيف يعمل</div>
          <h2 className="mt-3">ثلاث خطوات، ولا واحدة منها تحتاج تدريبًا.</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { n: '1', t: 'لوحة ورمز', d: 'نطبع لمحلك لوحة صغيرة فيها رمز. الزبون يمسحه بجواله، يختار باقته، ويحجز عضويته في أقل من دقيقة. يدفع لك عند الكرسي في أول زيارة.' },
            { n: '2', t: 'بطاقة على واتساب', d: 'تصله بطاقة عضوية برقمه ورصيده وتاريخ التجديد. ما يحتاج تطبيق ولا حساب ولا كلمة سر.' },
            { n: '3', t: 'زر واحد عند الكرسي', d: 'الحلاق يكتب رقم عضوية الزبون ويضغط «سجّل الزيارة». تنقص من حلاقاته فورًا، والسجل ما يتعدّل.' },
          ].map((s, i) => (
            <motion.div key={s.n} {...F} transition={{ ...F.transition, delay: i * 0.08 }} className="card p-6">
              <div className="display num inline-block bg-accent text-ink rounded-[var(--radius-md)] px-3 leading-none py-1" style={{ fontSize: '2.2rem' }}>{s.n}</div>
              <h3 className="mt-2">{s.t}</h3>
              <p className="muted mt-2">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHAT CHANGES */}
      <section className="container section pt-0">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <motion.div {...F} className="md:col-span-5 md:sticky md:top-8">
            <div className="eyebrow">ماذا يتغير في محلك</div>
            <h2 className="mt-3">أربعة أشياء تحس بها من أول شهر.</h2>
            <p className="muted mt-4">كل رقم في لوحتك محسوب من زيارات مؤكدة ودفعات حقيقية، لا من تقدير.</p>
          </motion.div>
          <div className="md:col-span-7 grid gap-4">
            {[
              { t: 'مدفوع مقدمًا', d: 'الأعضاء يدفعون أول الشهر قبل أن يجلسوا على الكرسي، وتشوف في لوحتك كم دخل من اشتراكات هذه الدورة.' },
              { t: 'الصباح يشتغل', d: 'الباقة الصباحية سعر دائم في قائمة أسعارك لمن يحضر بين 9 صباحًا و3 عصرًا، وأنت تحدد سعرها ونافذتها. الكرسي الفاضي يمتلئ بدل أن ينتظر الليل.' },
              { t: 'دفتر زبائنك', d: 'أسماء وأرقام وتاريخ زيارات، ملكك أنت وتصدّره متى شئت. ومن غاب 20 يومًا يصله تذكير باسم محلك.' },
              { t: 'بلا شغل إضافي', d: 'شاشة واحدة لمحلك تُفتح من متصفح جوالك برمز من 6 أرقام. لا أجهزة ولا تثبيت ولا تدريب، والنظام هو الذي يقول «لا» للزيارة قبل وقتها، لا أنت.' },
            ].map((b, i) => (
              <motion.div key={b.t} {...F} transition={{ ...F.transition, delay: i * 0.06 }} className="flex gap-4 border-t border-line pt-5">
                <div className="shrink-0 mt-2 size-3 rounded-sm bg-accent" aria-hidden="true" />
                <div><h3>{b.t}</h3><p className="muted mt-1">{b.d}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MORNING */}
      <section className="section bg-paper-2">
        <div className="container grid md:grid-cols-12 gap-8 items-center">
          <motion.div {...F} className="md:col-span-6">
            <div className="eyebrow">الباقة الصباحية</div>
            <h2 className="mt-3">الصباح له سعره، وله زبونه.</h2>
            <p className="mt-4 text-lg muted">
              بين 9 صباحًا و3 عصرًا محلك هادئ. الباقة الصباحية تُنشر من اليوم الأول كسعر دائم في قائمة أسعارك لمن يحضر في هذا الوقت: الموظف بدوام صباحي ينتهي 2، والطالب، ومن نهاره فاضٍ. تبيع الساعات الهادئة بدل أن تخسرها.
            </p>
          </motion.div>
          <motion.div {...F} className="md:col-span-6">
            <div className="card p-6">
              <div className="flex items-center justify-between"><h3>وسيط الساعات الهادئة لمحل الحلاقة في الرياض</h3><span className="pill">مسح 5,767 محلًا</span></div>
              <div className="display num mt-3" style={{ fontSize: '3.2rem' }}>17 <span className="text-lg font-normal muted">ساعة في الأسبوع</span></div>
              <TicksChart />
              <p className="muted text-sm mt-3">الذروة الأشيع 10 مساءً، والنافذة الهادئة من 9 صباحًا إلى 2 ظهرًا ثابتة في أغلب الأيام. محسوبة من بيانات الازدحام في خرائط قوقل، سبتمبر 2026.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="container section">
        <motion.div {...F} className="max-w-2xl">
          <div className="eyebrow">اشتراك محلك في تيمبو</div>
          <h2 className="mt-3">سعر واحد، لا يبدأ قبل عضوك العاشر. ولا هللة من اشتراكات زبائنك.</h2>
          <p className="muted mt-3">أسعار باقات زبائنك تحددها أنت من سعر حلاقتك. اشتراكات الأعضاء تذهب إلى حساب محلك مباشرة، أو تُدفع عندك على الكاونتر وتسجلها أنت.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {TIERS.map((t, i) => (
            <motion.div key={t.code} {...F} transition={{ ...F.transition, delay: i * 0.08 }} className={`card p-6 flex flex-col ${'featured' in t && t.featured ? 'border-ink ring-1 ring-ink' : ''}`}>
              <div className="flex items-center justify-between"><h3>{t.name}</h3>{'featured' in t && t.featured && <span className="pill pill-ok">10 مقاعد فقط</span>}</div>
              <div className="mt-3 num">{t.price === null ? <span className="display" style={{ fontSize: '2rem' }}>بالطلب</span> : <span className="display" style={{ fontSize: '2.6rem' }}>{t.price}</span>}{t.price ? <span className="muted"> ريالًا {t.period}</span> : null}</div>
              <div className="muted text-sm">{t.price ? '' : t.period}</div>
              <ul className="mt-5 grid gap-2 text-[15px]">
                {t.bullets.map((b) => <li key={b} className="beat ps-5">{b}</li>)}
              </ul>
              {t.code === 'chain' ? <a href={CONTACT_WA('السلام عليكم، عندي أكثر من فرع وأبي أعرف عن تيمبو.')} target="_blank" rel="noreferrer" className="btn mt-6 btn-ghost">كلمنا عن فروعك</a> : <Link to="/start" className={`btn mt-6 ${'featured' in t && t.featured ? 'btn-accent' : 'btn-ghost'}`}>{t.code === 'founding' ? 'احجز مقعد شريك تأسيس' : 'ابدأ بتيمبو'}</Link>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section bg-ink text-paper">
        <div className="container grid md:grid-cols-12 gap-8">
          <motion.div {...F} className="md:col-span-4"><div className="eyebrow">ثلاثة أسئلة نسمعها دائمًا</div><h2 className="mt-3 text-paper">وأجوبتها قصيرة.</h2></motion.div>
          <div className="md:col-span-8 grid gap-6">
            {[
              { q: 'الفلوس تمر على من؟', a: 'ما تمر على أحد. الزبون يدفع لك في المحل كاش أو مدى كما يدفع اليوم، والحلاق يسجل الدفع وأول حلاقة بزر واحد. تيمبو دفتر العضويات والزيارات، لا صرّاف.' },
              { q: 'ولو الزبون دفع وما جا؟', a: 'تنتقل له زيارة واحدة فقط إلى الشهر التالي، وهذا مكتوب له قبل الحجز. والنظام يذكّره باسم محلك بعد 20 يومًا بلا زيارة، لأن الزبون الذي يستخدم اشتراكه هو الذي يجدده.' },
              { q: 'أقدر أرسل عروضي لزبائني؟', a: 'نعم. كل زبون يوافق عند الاشتراك على استقبال رسائل محلك يدخل قاعدة عملائك، وترسل لهم من شاشة المحل رسالة واتساب باسمك: عرض، موعد إجازة، خدمة جديدة. من يرسل كلمة «إيقاف» يخرج فورًا، والنظام يحدّ عدد الرسائل حتى لا يملّ زبونك.' },
              { q: 'أحتاج تطبيقًا أو جهازًا؟', a: 'لا. شاشة المحل تُفتح من متصفح أي جوال أو تابلت برمز من 6 أرقام، والزبون يحتاج واتساب فقط. ولو زبونك يفضل الكاش، يدفع عندك وتسجله من الشاشة نفسها.' },
            ].map((f, i) => (
              <motion.div key={f.q} {...F} transition={{ ...F.transition, delay: i * 0.06 }} className="border-t border-paper/15 pt-5">
                <h3 className="text-paper">{f.q}</h3><p className="mt-2 text-paper/80">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="container section">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <motion.div {...F} className="md:col-span-5">
            <div className="eyebrow">نبدأ بمحل واحد</div>
            <h2 className="mt-3">أول 10 محلات تدخل معنا شركاء تأسيس: 6 أشهر بلا رسوم.</h2>
            <p className="muted mt-4">تسجل محلك في خمس خطوات قصيرة من جوالك، وتخرج بلوحة ورمز وباقات مقترحة. ثم نزورك في وقتك الهادئ، نركّب اللوحة، ونسجل أول عضو أمامك على الكرسي.</p>
          </motion.div>
          <motion.div {...F} className="md:col-span-7">
            <div className="card p-6 md:p-8 grid gap-4">
              <h3>ابدأ الآن، من جوالك</h3>
              <ol className="grid gap-2 text-[15px] list-decimal ps-5"><li>اسم المحل وسعر الحلاقة عندك</li><li>اسمك وجوالك، ورمز تحقق على واتساب</li><li>اختر خطتك (مقاعد التأسيس محدودة)</li><li>رمز دخولك، وتفتح لوحتك فورًا</li></ol>
              <Link to="/start" className="btn btn-accent btn-lg">ابدأ بمحلك الآن</Link>
              <a className="btn btn-ghost" href={CONTACT_WA('السلام عليكم، عندي سؤال عن تيمبو.')} target="_blank" rel="noreferrer">أو راسلنا على واتساب</a>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function HeroArt({ reduce }: { reduce: boolean }) {
  // Brand art: the wordmark on the accent panel, and four visits that fill in one after another (a month at the shop).
  return (
    <div className="relative mx-auto w-full max-w-[420px]">
      <style>{`@keyframes fillin{0%,10%{background:var(--color-paper)}30%,100%{background:var(--color-ink)}}`}</style>
      <div className="rounded-[var(--radius-lg)] bg-accent p-8 md:p-10 aspect-[4/3] flex flex-col justify-between" dir="ltr">
        <Wordmark height={76} />
        <div className="flex items-center gap-3" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="block size-7 rounded-full border-[3px] border-ink" style={reduce ? { background: 'var(--color-ink)' } : { animation: `fillin 4.8s ${i * 1.2}s infinite` }} />
          ))}
          <span className="ms-auto text-ink font-bold text-sm" dir="rtl">4 حلاقات · شهر واحد</span>
        </div>
      </div>
      <div className="absolute -bottom-4 inset-x-0 flex justify-center">
        <div className="card px-4 py-2 text-sm font-semibold">كل أسبوعين، على الكرسي نفسه</div>
      </div>
    </div>
  );
}

function TicksChart() {
  // Hour-of-day typical busyness for a men's barbershop in Riyadh (shape derived from Google popular-times data; see docs/00-brief).
  const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  const level = [8, 10, 12, 14, 16, 20, 30, 42, 55, 68, 80, 92, 100, 96, 70];
  return (
    <div className="mt-4">
      <div className="flex items-end gap-1 h-28" aria-hidden="true">
        {hours.map((h, i) => (
          <div key={h} className="flex-1 rounded-t-[4px]" style={{ height: `${level[i]}%`, background: h < 15 ? 'var(--color-accent)' : 'var(--color-ink)', opacity: h < 15 ? 1 : 0.85 }} title={`${h}:00`} />
        ))}
      </div>
      <div className="flex justify-between text-xs muted mt-1 num"><span>9 صباحًا</span><span>3 عصرًا</span><span>10 مساءً</span></div>
      <div className="text-xs mt-2"><span className="inline-block size-2 rounded-sm bg-accent align-middle me-1" /> نافذة الباقة الصباحية</div>
    </div>
  );
}

